import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Grid,
  TextField,
  IconButton,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  EventBusyRounded as EventBusyIcon,
  AddCircleOutline as AddIcon,
  DeleteOutline as DeleteIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import { BookingConfig, ExcludedDay } from '../../types';

interface ExcludedDaysFormProps {
  formData: Partial<BookingConfig>;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string | null>;
  /** IANA timezone from booking config e.g. 'Asia/Kolkata'. Falls back to local browser date. */
  timezone?: string;
  forceExpand?: boolean;
}

export const ExcludedDaysForm: React.FC<ExcludedDaysFormProps> = ({
  formData,
  onChange,
  errors,
  timezone,
  forceExpand = false,
}) => {
  const excludedDays: ExcludedDay[] = formData.excluded_days?.days || [];
  const [expanded, setExpanded] = useState(excludedDays.length > 0);

  useEffect(() => {
    if (forceExpand) {
      setExpanded(true);
    }
  }, [forceExpand]);

  const handleAddDay = () => {
    const updated = [
      ...excludedDays,
      {
        day: '',
        start_time: null,
        end_time: null,
        message: '',
      },
    ];
    onChange('excluded_days', { days: updated });
  };

  const handleRemoveDay = (idx: number) => {
    const updated = excludedDays.filter((_, i) => i !== idx);
    onChange('excluded_days', { days: updated });
  };

  const handleFieldChange = (idx: number, field: keyof ExcludedDay, value: string) => {
    const updated = excludedDays.map((entry, i) =>
      i === idx ? { ...entry, [field]: value } : entry
    );
    onChange('excluded_days', { days: updated });
  };

  // "Today" in the business config timezone — used for min= on the date picker
  // (grays out past dates) and for isPastDate comparison.
  // Using Intl.DateTimeFormat so the date is always correct for the SMB's TZ,
  // regardless of the browser/VM system clock timezone.
  const todayStr = (() => {
    const now = new Date();
    if (timezone) {
      try {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: timezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
        }).formatToParts(now);
        const get = (t: string) => parts.find(p => p.type === t)?.value ?? '0';
        return `${get('year')}-${get('month')}-${get('day')}`;
      } catch { /* fall through */ }
    }
    // Fallback: local browser date parts (avoids toISOString UTC-shift)
    return [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');
  })();

  // Compare YYYY-MM-DD strings directly — lexicographic == chronological, no Date() needed.
  const isPastDate = (dateStr: string) => !!dateStr && dateStr < todayStr;

  return (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <Accordion
        expanded={expanded}
        onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
        disableGutters
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 4, py: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EventBusyIcon color="primary" fontSize="small" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Holidays &amp; Excluded Days
            </Typography>
            {excludedDays.length > 0 && (
              <Chip
                label={`${excludedDays.length} day${excludedDays.length > 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 4, pb: 4 }}>
          <Typography color="textSecondary" sx={{ mb: 3 }}>
            Block specific dates from booking.
          </Typography>

          {excludedDays.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography color="textSecondary" variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                No excluded days configured. Bookings are available on all working days.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {[...excludedDays]
                .sort((a, b) => {
                  if (!a.day) return 1;
                  if (!b.day) return -1;
                  return a.day.localeCompare(b.day); // YYYY-MM-DD sorts lexicographically
                })
                .map((entry, idx) => {
                  // Track original index so edits/deletes target the correct array slot
                  const originalIdx = excludedDays.indexOf(entry);
                  const dayError = errors[`excluded_day_${originalIdx}_day`];
                  const past = isPastDate(entry.day);

                  return (
                    <Box
                      key={idx}
                      data-booking-error-key={`excluded_day_${originalIdx}_day`}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: dayError ? 'error.main' : 'divider',
                        bgcolor: past ? 'action.hover' : 'transparent',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                      }}
                    >
                      <Grid container spacing={2} alignItems="flex-start">
                        {/* Date */}
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Date"
                            type="date"
                            size="small"
                            value={entry.day}
                            onChange={(e) => handleFieldChange(originalIdx, 'day', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: todayStr }}
                            error={!!dayError}
                            helperText={dayError}
                          />
                          {past && !dayError && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                              <Typography variant="caption" color="warning.main">
                                Past date
                              </Typography>
                            </Box>
                          )}
                        </Grid>

                        {/* Message */}
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            label="Reason (optional)"
                            size="small"
                            value={entry.message}
                            onChange={(e) => handleFieldChange(originalIdx, 'message', e.target.value)}
                            placeholder="e.g. Diwali Holiday"
                          />
                        </Grid>

                        {/* Delete */}
                        <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveDay(originalIdx)}
                              sx={{
                                border: '1px solid',
                                borderColor: 'error.light',
                                '&:hover': { bgcolor: 'error.50' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
            </Box>
          )}

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleAddDay}
            sx={{ borderRadius: '20px', textTransform: 'none' }}
          >
            Add Exclusion
          </Button>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};
